/**
 * Tenant Security Component
 * Container component for managing tenant security policies and user authentication status.
 * Provides tab navigation between Tenant Policy and User Status views.
 *
 * Phase 3 - TAP Passwordless Authentication
 */

import React, { useState, useEffect, useRef } from 'react';
import { useQueryClient, useIsFetching } from '@tanstack/react-query';
import {
  Add20Regular,
  ArrowClockwise20Regular,
  Delete24Regular,
} from '@fluentui/react-icons';
import { ConditionalAccessPolicies, type ConditionalAccessPoliciesHandle } from '../components/TenantSecurity/ConditionalAccess/ConditionalAccessPolicies';
import { PolicyConfigModal } from '../components/TenantSecurity/ConditionalAccess/PolicyConfigModal';
import { AuthenticationMethodsConfig } from '../components/TenantSecurity/AuthenticationMethods/AuthenticationMethodsConfig';
import { CrossTenantAccessSettings } from '../components/TenantSecurity/CrossTenantAccess';
import { RegistrationCampaignCard } from '../components/TenantSecurity/TenantPolicy/RegistrationCampaignCard';
import { TransportRulesPage, type TransportRulesPageHandle } from '../components/TenantSecurity/TransportRules/TransportRulesPage';
import { TransportRuleModal, type TransportRuleInitialData } from '../components/TenantSecurity/TransportRules/TransportRuleModal';
import { ExchangeRulesApiService } from '../services/transportRulesApi';
import type { TransportRule, ExchangeRuleRequest, ExchangeRuleUpdateRequest } from '../types/transportRules';
import { getRuleType } from '../types/transportRules';
import { TenantContextSelector } from '../components/TenantManagement/TenantContextSelector';
import { useTenantContext } from '../contexts/TenantContext';
import { Alert } from '../components/common/Alert';
import {
  PageLayout,
  DataGridToolbar,
  ToolbarButton,
} from '../components/layouts';
import { authenticationMethodsQueryKey } from '../hooks/useAuthenticationMethods';
import { conditionalAccessPoliciesQueryKey } from '../hooks/useConditionalAccessPolicies';
import { transportRulesQueryKey } from '../hooks/useTransportRules';

type SecurityTab = 'policy' | 'conditionalAccess' | 'authMethods' | 'crossTenant' | 'transportRules';

export const TenantSecurityPage: React.FC = () => {
  const { currentTenant } = useTenantContext();
  const queryClient = useQueryClient();

  // Track fetching states for refresh button loading indicators
  const isFetchingConditionalAccess = useIsFetching({
    queryKey: currentTenant ? conditionalAccessPoliciesQueryKey(currentTenant.tenant_id) : [],
  });
  const isFetchingAuthMethods = useIsFetching({
    queryKey: currentTenant ? authenticationMethodsQueryKey(currentTenant.tenant_id) : [],
  });
  const isFetchingTransportRules = useIsFetching({
    queryKey: currentTenant ? transportRulesQueryKey(currentTenant.tenant_id) : [],
  });

  const [selectedTab, setSelectedTab] = useState<SecurityTab>(() => {
    // Restore last selected tab from localStorage
    const saved = localStorage.getItem('securitySelectedTab');
    return (saved as SecurityTab) || 'policy';
  });

  const [policyConfigModalOpen, setPolicyConfigModalOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Selection state for Conditional Access policies
  const [selectedPolicies, setSelectedPolicies] = useState<Set<string>>(new Set());
  const [policyCount, setPolicyCount] = useState(0);

  // Selection state for Transport Rules
  const [selectedTransportRules, setSelectedTransportRules] = useState<Set<string>>(new Set());
  const [transportRuleCount, setTransportRuleCount] = useState(0);
  const [transportRuleModalOpen, setTransportRuleModalOpen] = useState(false);
  const [transportRuleModalMode, setTransportRuleModalMode] = useState<'create' | 'edit'>('create');
  const [editingTransportRuleData, setEditingTransportRuleData] = useState<TransportRuleInitialData | undefined>(undefined);

  // Ref for ConditionalAccessPolicies component
  const conditionalAccessPoliciesRef = useRef<ConditionalAccessPoliciesHandle>(null);

  // Ref for TransportRulesPage component
  const transportRulesPageRef = useRef<TransportRulesPageHandle>(null);

  // Auto-dismiss messages after 5 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Save tab selection to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('securitySelectedTab', selectedTab);
  }, [selectedTab]);

  const handleCreatePolicy = () => {
    setPolicyConfigModalOpen(true);
  };

  const handlePolicyCreated = () => {
    setSuccess('Conditional Access policy created successfully');
    // Invalidate conditional access policies cache to trigger refetch
    if (currentTenant) {
      queryClient.invalidateQueries({
        queryKey: conditionalAccessPoliciesQueryKey(currentTenant.tenant_id),
      });
    }
  };

  const handleAuthMethodsRefresh = () => {
    if (currentTenant) {
      queryClient.invalidateQueries({
        queryKey: authenticationMethodsQueryKey(currentTenant.tenant_id),
      });
    }
  };

  const handleConditionalAccessRefresh = () => {
    if (currentTenant) {
      queryClient.invalidateQueries({
        queryKey: conditionalAccessPoliciesQueryKey(currentTenant.tenant_id),
      });
    }
  };

  const handleBulkDeletePolicies = () => {
    // Trigger bulk delete dialog in ConditionalAccessPolicies component via ref
    conditionalAccessPoliciesRef.current?.openBulkDeleteDialog();
  };

  const handleSelectionChange = (selection: Set<string>) => {
    setSelectedPolicies(selection);
  };

  const handlePolicyCountChange = (count: number) => {
    setPolicyCount(count);
  };

  const handleTransportRulesRefresh = () => {
    if (currentTenant) {
      queryClient.invalidateQueries({
        queryKey: transportRulesQueryKey(currentTenant.tenant_id),
      });
    }
  };

  const handleTransportRuleSelectionChange = (selection: Set<string>) => {
    setSelectedTransportRules(selection);
  };

  const handleTransportRuleCountChange = (count: number) => {
    setTransportRuleCount(count);
  };

  const handleBulkDeleteTransportRules = () => {
    // Trigger bulk delete dialog in TransportRulesPage component via ref
    transportRulesPageRef.current?.openBulkDeleteDialog();
  };

  const handleCreateTransportRule = () => {
    setTransportRuleModalMode('create');
    setEditingTransportRuleData(undefined);
    setTransportRuleModalOpen(true);
  };

  const handleEditTransportRule = (rule: TransportRule) => {
    // Convert TransportRule to TransportRuleInitialData
    const ruleType = getRuleType(rule);

    // Extract client domain from FromAddressMatchesPatterns if rule type is block-client-domain
    let clientDomain: string | undefined;
    if (ruleType === 'block-client-domain' && rule.FromAddressMatchesPatterns && rule.FromAddressMatchesPatterns.length > 0) {
      // Pattern is "@domain.com", extract domain
      const pattern = rule.FromAddressMatchesPatterns[0];
      clientDomain = pattern.replace(/^@/, '');
    }

    // Extract allowed domains from ExceptIfRecipientDomainIs if rule type is block-external-senders
    let allowedDomains: string[] | undefined;
    if (ruleType === 'block-external-senders' && rule.ExceptIfRecipientDomainIs) {
      allowedDomains = Array.isArray(rule.ExceptIfRecipientDomainIs)
        ? rule.ExceptIfRecipientDomainIs
        : [rule.ExceptIfRecipientDomainIs];
    }

    const initialData: TransportRuleInitialData = {
      ruleId: rule.Identity,  // Store the rule ID for updates
      ruleType: ruleType === 'unknown' ? 'block-external-senders' : ruleType,
      name: rule.Name,
      clientDomain,
      allowedDomains,
      priority: rule.Priority,
      enabled: rule.State === 'Enabled',
    };

    setTransportRuleModalMode('edit');
    setEditingTransportRuleData(initialData);
    setTransportRuleModalOpen(true);
  };

  const handleTransportRuleSubmit = async (data: ExchangeRuleRequest | ExchangeRuleUpdateRequest, ruleId?: string) => {
    if (!currentTenant) return;

    try {
      if (transportRuleModalMode === 'create') {
        // Create mode: data is ExchangeRuleRequest
        await ExchangeRulesApiService.createRule(currentTenant.tenant_id, data as ExchangeRuleRequest);
        setSuccess('Transport rule created successfully');
      } else {
        // Edit mode: data is ExchangeRuleUpdateRequest, ruleId is required
        if (!ruleId) {
          throw new Error('Rule ID is required for editing');
        }
        await ExchangeRulesApiService.updateRule(currentTenant.tenant_id, ruleId, data as ExchangeRuleUpdateRequest);
        setSuccess('Transport rule updated successfully');
      }

      setTransportRuleModalOpen(false);
      // Invalidate transport rules cache to trigger refetch
      queryClient.invalidateQueries({
        queryKey: transportRulesQueryKey(currentTenant.tenant_id),
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save transport rule';
      setError(errorMessage);
      throw err; // Re-throw to let modal handle the error
    }
  };

  /**
   * Render Tenant Policy tab content (no toolbar)
   */
  const renderPolicyContent = () => {
    if (!currentTenant) return null;
    return (
      <RegistrationCampaignCard tenantId={currentTenant.tenant_id} />
    );
  };

  /**
   * Render Conditional Access tab content with toolbar
   */
  const renderConditionalAccessContent = () => {
    if (!currentTenant) return null;
    return (
      <>
        <DataGridToolbar>
          <DataGridToolbar.Left>
            <ToolbarButton
              icon={<ArrowClockwise20Regular />}
              onClick={handleConditionalAccessRefresh}
              loading={isFetchingConditionalAccess > 0}
              loadingText="Refreshing..."
              data-testid="refresh-policies-button"
            >
              Refresh
            </ToolbarButton>
          </DataGridToolbar.Left>
          <DataGridToolbar.Right>
            {policyCount > 0 && (
              <ToolbarButton
                icon={<Delete24Regular />}
                onClick={handleBulkDeletePolicies}
                disabled={selectedPolicies.size === 0}
                data-testid="bulk-delete-button"
              >
                {selectedPolicies.size > 0 ? `Delete (${selectedPolicies.size})` : 'Delete'}
              </ToolbarButton>
            )}
            <ToolbarButton
              appearance="primary"
              icon={<Add20Regular />}
              onClick={handleCreatePolicy}
              data-testid="create-policy-button"
            >
              Create Policy
            </ToolbarButton>
          </DataGridToolbar.Right>
        </DataGridToolbar>
        <ConditionalAccessPolicies
          ref={conditionalAccessPoliciesRef}
          tenantId={currentTenant.tenant_id}
          onSelectionChange={handleSelectionChange}
          onPolicyCountChange={handlePolicyCountChange}
        />
      </>
    );
  };

  /**
   * Render Authentication Methods tab content with toolbar
   */
  const renderAuthMethodsContent = () => {
    if (!currentTenant) return null;
    return (
      <>
        <DataGridToolbar>
          <DataGridToolbar.Left>
            <ToolbarButton
              icon={<ArrowClockwise20Regular />}
              onClick={handleAuthMethodsRefresh}
              loading={isFetchingAuthMethods > 0}
              loadingText="Refreshing..."
              data-testid="refresh-auth-methods-button"
            >
              Refresh
            </ToolbarButton>
          </DataGridToolbar.Left>
        </DataGridToolbar>
        <AuthenticationMethodsConfig tenantId={currentTenant.tenant_id} />
      </>
    );
  };

  /**
   * Render Cross-Tenant Access tab content
   */
  const renderCrossTenantContent = () => {
    if (!currentTenant) return null;
    return (
      <CrossTenantAccessSettings tenantId={currentTenant.tenant_id} />
    );
  };

  /**
   * Render Transport Rules tab content with toolbar
   */
  const renderTransportRulesContent = () => {
    if (!currentTenant) return null;
    return (
      <>
        <DataGridToolbar>
          <DataGridToolbar.Left>
            <ToolbarButton
              icon={<ArrowClockwise20Regular />}
              onClick={handleTransportRulesRefresh}
              loading={isFetchingTransportRules > 0}
              loadingText="Refreshing..."
              data-testid="refresh-transport-rules-button"
            >
              Refresh
            </ToolbarButton>
          </DataGridToolbar.Left>
          <DataGridToolbar.Right>
            <ToolbarButton
              icon={<Delete24Regular />}
              onClick={handleBulkDeleteTransportRules}
              disabled={selectedTransportRules.size === 0}
              data-testid="bulk-delete-transport-rules-button"
            >
              {selectedTransportRules.size > 0 ? `Delete (${selectedTransportRules.size})` : 'Delete'}
            </ToolbarButton>
            <ToolbarButton
              appearance="primary"
              icon={<Add20Regular />}
              onClick={handleCreateTransportRule}
              data-testid="create-transport-rule-button"
            >
              Create Rule
            </ToolbarButton>
          </DataGridToolbar.Right>
        </DataGridToolbar>
        <TransportRulesPage
          ref={transportRulesPageRef}
          tenantId={currentTenant.tenant_id}
          onSelectionChange={handleTransportRuleSelectionChange}
          onRuleCountChange={handleTransportRuleCountChange}
          onEdit={handleEditTransportRule}
        />
      </>
    );
  };

  return (
    <PageLayout
      title="Tenant Security"
      tabs={[
        { value: 'policy', label: 'Tenant Policy' },
        { value: 'conditionalAccess', label: 'Conditional Access' },
        { value: 'authMethods', label: 'Authentication Methods' },
        { value: 'crossTenant', label: 'Cross-Tenant Access' },
        { value: 'transportRules', label: 'Transport Rules' },
      ]}
      selectedTab={selectedTab}
      onTabChange={(value) => setSelectedTab(value as SecurityTab)}
      headerActions={<TenantContextSelector />}
      testId="tenant-security"
    >
      {/* Alert Messages */}
      {error && <Alert intent="error">{error}</Alert>}
      {success && <Alert intent="success">{success}</Alert>}

      {/* Show message if no tenant selected */}
      {!currentTenant && (
        <Alert intent="warning" title="No Tenant Selected">
          Please select a tenant from the dropdown above to manage
          tenant security settings.
        </Alert>
      )}

      {/* Tab Content */}
      {selectedTab === 'policy' && renderPolicyContent()}
      {selectedTab === 'conditionalAccess' && renderConditionalAccessContent()}
      {selectedTab === 'authMethods' && renderAuthMethodsContent()}
      {selectedTab === 'crossTenant' && renderCrossTenantContent()}
      {selectedTab === 'transportRules' && renderTransportRulesContent()}

      {/* Modals */}
      {currentTenant && (
        <PolicyConfigModal
          isOpen={policyConfigModalOpen}
          onClose={() => setPolicyConfigModalOpen(false)}
          tenantId={currentTenant.tenant_id}
          onSuccess={() => {
            setPolicyConfigModalOpen(false);
            handlePolicyCreated();
          }}
        />
      )}

      <TransportRuleModal
        mode={transportRuleModalMode}
        isOpen={transportRuleModalOpen}
        onClose={() => setTransportRuleModalOpen(false)}
        onSubmit={handleTransportRuleSubmit}
        initialData={editingTransportRuleData}
        totalRuleCount={transportRuleCount}
      />
    </PageLayout>
  );
};
